import { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import { ExclamationTriangleIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/20/solid';


export default function PendingInvite({ userId }) {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    const fetchUserEmail = async () => {
      try {
        const { data, error } = await supabase
          .from('profile')
          .select('email_address')
          .eq('id', userId)
          .single();

        if (error) throw error;

        setUserEmail(data.email_address);
      } catch (err) {
        setError('Failed to fetch user email.');
        console.error(err.message);
      }
    };

    if (userId) {
      fetchUserEmail();
    }
  }, [userId]);

  useEffect(() => {
    const fetchPendingInvites = async () => {
      if (!userEmail) return;

      setLoading(true);
      setError('');
      try {
        const { data, error } = await supabase
          .from('organization_invites')
          .select('id, organization_id, organizations(name)')
          .eq('email', userEmail)
          .eq('status', 'pending');

        if (error) throw error;

        setInvites(data || []);
      } catch (err) {
        setError('Failed to fetch pending invites.');
        console.error(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPendingInvites();
  }, [userEmail]);

  const handleAcceptInvite = async (inviteId, organizationId) => {
    setLoading(true);
    setError('');
    setSuccessMessage('');
    try {
      const { error } = await supabase
        .from('organization_members')
        .insert([{ user_id: userId, organization_id: organizationId, role: 'member' }]);

      if (error) throw error;

      const { error: updateError } = await supabase
        .from('organization_invites')
        .update({ status: 'accepted' })
        .eq('id', inviteId);

      if (updateError) throw updateError;

      setInvites((prev) => prev.filter((invite) => invite.id !== inviteId));
      setSuccessMessage('Successfully joined the organization!');
      window.location.reload();
    } catch (err) {
      setError('Failed to accept the invite. Please try again.');
      console.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelInvite = async (inviteId) => {
    setLoading(true);
    setError('');
    setSuccessMessage('');
    try {
      const { error } = await supabase
        .from('organization_invites')
        .update({ status: 'cancelled' })
        .eq('id', inviteId);

      if (error) throw error;

      setInvites((prev) => prev.filter((invite) => invite.id !== inviteId));
      setSuccessMessage('Invite cancelled successfully.');
    window.location.reload();
    } catch (err) {
      setError('Failed to cancel the invite. Please try again.');
      console.error(err.message);
    } finally {
      setLoading(false);
    }
    
  };

  if (loading) {
    return (
      <div>
      </div>
    );
  }

  return (
    <div className="mt-6">
      {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
      {successMessage && <p className="text-sm text-green-600 mt-2">{successMessage}</p>}
      {invites.length === 0 ? (
       <div></div>
      ) : (
        <div className="space-y-4">
          {invites.map((invite) => (
            <div
              key={invite.id}
              className="border-l-4 border-yellow-400 bg-yellow-50 p-4  shadow-sm"
            >
              <div className="flex items-center">
                <div className="shrink-0">
                  <ExclamationTriangleIcon
                    aria-hidden="true"
                    className="h-5 w-5 text-yellow-400"
                  />
                </div>
                <div className="ml-3 flex flex-col sm:flex-row sm:items-center sm:justify-between w-full">
                  <p className="text-sm text-yellow-700">
                    <strong>{invite.organizations.name}</strong> has invited you to join
                    their organization.
                  </p>
                  <div className="flex items-center mt-2 sm:mt-0 sm:ml-4">
                  <button
                    onClick={() => handleAcceptInvite(invite.id, invite.organization_id)}
                    className="flex items-center justify-center mr-2 rounded-md border border-yellow-400 bg-yellow-200 px-4 py-2 text-sm font-semibold text-yellow-700 shadow-sm hover:bg-yellow-100"
                    >
                    <CheckCircleIcon className="h-5 w-5 mr-2 text-yellow-700" />
                    Accept
                    </button>
                    <button
                      onClick={() => handleCancelInvite(invite.id)}
                      className="flex items-center justify-center mr-2 rounded-md border border-yellow-400 bg-yellow-200 px-4 py-2 text-sm font-semibold text-yellow-700 shadow-sm hover:bg-yellow-100"
                      >
                    <XCircleIcon className="h-5 w-5 mr-2 text-yellow-700" />
                      Decline
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
